import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'

import { useAuth } from '@/features/auth'
import { api, ApiError, queryClient } from '@/shared/api'
import { routes } from '@/shared/config'
import { typographyStyle } from '@/shared/lib/catalog'
import { designTokens } from '@/shared/lib/design-tokens'
import { Button, Checkbox } from '@/shared/ui'
import { SuperAdminTabNav } from './super-admin-tab-nav'

type MigrationSource = {
  sourceType: string
  label: string
  category: 'identity' | 'subscription' | 'billing' | 'reference' | 'history' | 'integration'
  localStorage: string
  notes: string
  sourceCount: number | null
  localImportedCount: number
  identityMapCount: number
  latestSourceCreatedAt: string | null
  latestSourceModifiedAt: string | null
  latestRunSummary: Record<string, number>
}

type MigrationRun = {
  id: string
  runKey: string
  mode: string
  status: string
  scope: Record<string, unknown>
  totals: Record<string, unknown>
  errorMessage?: string | null
  startedAt?: string | null
  finishedAt?: string | null
  createdAt: string
  updatedAt: string
}

type ActiveMigrationJob = {
  jobId: string
  operation: string
  status: 'queued' | 'running' | 'cancel_requested' | 'cancelled' | 'completed' | 'failed' | 'stale'
  requestedByUserId?: string | null
  runId?: string | null
  totals: Record<string, unknown>
  progress: Record<string, unknown>
  errorMessage?: string | null
  sourceTypes: string[]
  pageLimit: number
  maxPagesPerSource?: number | null
  cancelRequestedAt?: string | null
  heartbeatAt?: string | null
  startedAt: string
  updatedAt: string
  finishedAt?: string | null
}

type ReportArtifact = {
  id: string
  runId: string
  artifactType: string
  format: string
  storageKind: string
  contentHash?: string | null
  generatedAt: string
}

type AuditEvent = {
  id: string
  runId?: string | null
  actorUserId?: string | null
  eventType: string
  message: string
  metadata: Record<string, unknown>
  createdAt: string
}

type MigrationOverview = {
  sourceCatalog: MigrationSource[]
  latestRun: MigrationRun | null
  latestDryRun: MigrationRun | null
  localTotals: {
    usersWithBubbleId: number
    servicesWithBubbleId: number
    pricesWithBubbleId: number
    operationEvents: number
    paymentFacts: number
    paymentProjections: number
  }
  paymentLineageSummary: {
    projectionsWithBubbleOperationId: number
    projectionsWithBubblePaymentId: number
    projectionsMissingBubblePaymentId: number
    operationDerivedProjections: number
  }
  relationSummary: Record<string, number>
  reportArtifacts: ReportArtifact[]
  auditEvents: AuditEvent[]
  actions: {
    dryRunEnabled: boolean
    liveRunEnabled: boolean
  }
  activeJob: ActiveMigrationJob | null
}

type MigrationOverviewResponse = {
  data: MigrationOverview
}

type DryRunResponse = {
  success: boolean
  data: {
    runId?: string
    jobId?: string
    status?: string
    totals?: Record<string, number>
  }
}

type EntityImportMode = 'dry_run' | 'live_run'

type EntityImportKey =
  | 'optionSets'
  | 'references'
  | 'users'
  | 'services'
  | 'paymentMethods'
  | 'operations'
  | 'payments'
  | 'subscriptionHistory'
  | 'supportHistory'

type EntityImportAction = {
  key: EntityImportKey
  label: string
  sourceTypes: string[]
  endpoint: string
  confirm: string
  scopeLabel: string
  dependsOn: string
  notes: string
  acceptsPagination: boolean
}

type EntityImportInput = {
  action: EntityImportAction
  mode: EntityImportMode
  pageLimit: number
  maxPagesPerSource?: number
}

type RunDetailResponse = {
  data: {
    run: MigrationRun
    manifests: Array<{
      id: string
      sourceType: string
      pageOffset?: number | null
      recordsSeen: number
      status: string
      latestSourceCreatedAt?: string | null
      latestSourceModifiedAt?: string | null
      createdAt: string
    }>
    entityResults: Array<{
      id: string
      sourceType: string
      sourceId?: string | null
      localTable?: string | null
      localId?: string | null
      action: string
      status: string
      conflictCode?: string | null
      conflictMessage?: string | null
      reportData: Record<string, unknown>
      createdAt: string
      updatedAt: string
    }>
    reportArtifacts: ReportArtifact[]
    relationSummary: Record<string, number>
    auditEvents: AuditEvent[]
  }
}

type ReportDetailResponse = {
  data: ReportArtifact & {
    objectKey?: string | null
    contentJson: Record<string, unknown> | null
  }
}

type DryRunFormState = {
  pageLimit: string
  maxPagesPerSource: string
  scanDepth: 'first_page' | 'all_pages'
}

type MigrationConsoleTab =
  | 'overview'
  | 'sources'
  | 'importSteps'
  | 'deltaSync'
  | 'runsReports'
  | 'conflicts'
  | 'audit'
  | 'diagnostics'

const migrationConsoleTabs: Array<{ key: MigrationConsoleTab; label: string }> = [
  { key: 'overview', label: 'Overview' },
  { key: 'sources', label: 'Sources' },
  { key: 'importSteps', label: 'Import Steps' },
  { key: 'deltaSync', label: 'Delta Sync' },
  { key: 'runsReports', label: 'Runs & Reports' },
  { key: 'conflicts', label: 'Conflicts' },
  { key: 'audit', label: 'Audit' },
  { key: 'diagnostics', label: 'Diagnostics' },
]

const titleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.strong,
  ...typographyStyle(designTokens.typography.heading.h2),
}

const subtitleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.primary,
  ...typographyStyle(designTokens.typography.body.base),
}

const sectionTitleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.strong,
  ...typographyStyle(designTokens.typography.body.baseSemibold),
}

const tableHeadStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.strong,
  ...typographyStyle(designTokens.typography.body.baseSemibold),
}

const cellStyle = {
  borderBottom: `${designTokens.borders.widths.thin} ${designTokens.borders.styles.solid} ${designTokens.colors.border.adminPattern}`,
  padding: `${designTokens.spacing.sm} ${designTokens.spacing.xs}`,
  verticalAlign: 'top' as const,
  fontFamily: designTokens.typography.fontFamilies.primary,
  ...typographyStyle(designTokens.typography.body.sm),
  color: designTokens.colors.text.primary,
}

const mutedTextStyle = {
  color: designTokens.colors.text.secondary,
  ...typographyStyle(designTokens.typography.body.xsNormal),
}

const inputStyle = {
  border: `${designTokens.borders.widths.thin} ${designTokens.borders.styles.solid} ${designTokens.colors.border.inputDefault}`,
  borderRadius: designTokens.borders.radius.sm,
  backgroundColor: designTokens.colors.background.surface,
  color: designTokens.colors.text.primary,
  minHeight: designTokens.sizes.height.paymentSummaryPromoInput,
  padding: `${designTokens.spacing.xs} ${designTokens.spacing.sm}`,
  fontFamily: designTokens.typography.fontFamilies.primary,
  ...typographyStyle(designTokens.typography.body.sm),
}

const statusBadgeStyle = (status: string) => {
  switch (status) {
    case 'completed':
    case 'synced':
    case 'fetched':
      return { background: '#dcfce7', color: '#166534' }
    case 'completed_with_warnings':
    case 'partial':
    case 'needs_review':
    case 'pending':
      return { background: '#fef9c3', color: '#854d0e' }
    case 'failed':
    case 'conflict':
      return { background: '#fee2e2', color: '#991b1b' }
    case 'running':
      return { background: '#dbeafe', color: '#1e3a8a' }
    default:
      return { background: '#f3f4f6', color: '#374151' }
  }
}

const formatNumber = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : new Intl.NumberFormat('ru-RU').format(value)

const formatDate = (value?: string | null) => {
  if (!value) {
    return '—'
  }

  return new Intl.DateTimeFormat('ru-RU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(value))
}

const actionSummary = (summary: Record<string, number>) => {
  const visibleEntries = ['created', 'updated', 'unchanged', 'skipped', 'conflict', 'failed']
    .map((key) => [key, summary[key] ?? 0] as const)
    .filter(([, value]) => value > 0)

  return visibleEntries.length > 0
    ? visibleEntries.map(([key, value]) => `${key}: ${formatNumber(value)}`).join(' · ')
    : '—'
}

const loadOverview = () =>
  api.get<MigrationOverviewResponse>('/api/v1/admin/bubble-migration/overview')

const loadRunDetail = (runId: string) =>
  api.get<RunDetailResponse>(`/api/v1/admin/bubble-migration/runs/${runId}`)

const loadReportDetail = (artifactId: string) =>
  api.get<ReportDetailResponse>(`/api/v1/admin/bubble-migration/reports/${artifactId}`)

const runDryRun = (input: {
  sourceTypes: string[]
  pageLimit: number
  maxPagesPerSource?: number
  runInBackground?: boolean
}) => api.post<DryRunResponse>('/api/v1/admin/bubble-migration/dry-run', input)

const cancelMigrationJob = (jobId: string) =>
  api.post<{ success: boolean; data: { activeJob: ActiveMigrationJob | null } }>(
    `/api/v1/admin/bubble-migration/jobs/${jobId}/cancel`,
    {}
  )

const referenceImportSourceTypes = new Set([
  'Price',
  'Tariff',
  'Discount',
  'Promocode',
  'Promotion',
  'Payattempt on Hold',
  'BotHelp',
])

const entityImportActions: EntityImportAction[] = [
  {
    key: 'optionSets',
    label: 'Option Sets',
    sourceTypes: ['OptionSet'],
    endpoint: '/api/v1/admin/bubble-migration/option-sets/import',
    confirm: 'IMPORT_OPTION_SETS',
    scopeLabel: 'reference',
    dependsOn: 'None',
    notes: 'Local reference rows from sanitized artifacts; no Bubble Data API call.',
    acceptsPagination: false,
  },
  {
    key: 'references',
    label: 'Global References',
    sourceTypes: Array.from(referenceImportSourceTypes),
    endpoint: '/api/v1/admin/bubble-migration/references/import',
    confirm: 'IMPORT_REFERENCES',
    scopeLabel: 'prices, tariffs, discounts, promo, retry ladder, BotHelp',
    dependsOn: 'Option Sets',
    notes: 'Imports reference facts only; sensitive values are redacted in reports.',
    acceptsPagination: true,
  },
  {
    key: 'users',
    label: 'Users And Catalog Links',
    sourceTypes: ['User'],
    endpoint: '/api/v1/admin/bubble-migration/users/import',
    confirm: 'IMPORT_USERS',
    scopeLabel: 'identity',
    dependsOn: 'Option Sets recommended',
    notes: 'Matches existing local users; does not create unmatched auth users.',
    acceptsPagination: true,
  },
  {
    key: 'services',
    label: 'Services',
    sourceTypes: ['Service'],
    endpoint: '/api/v1/admin/bubble-migration/services/import',
    confirm: 'IMPORT_SERVICES',
    scopeLabel: 'subscription projection',
    dependsOn: 'Option Sets, Global References, Users',
    notes: 'Updates local service projection and queues unresolved Bubble relations.',
    acceptsPagination: true,
  },
  {
    key: 'paymentMethods',
    label: 'Saved CloudPayments Cards',
    sourceTypes: ['User'],
    endpoint: '/api/v1/admin/bubble-migration/payment-methods/import',
    confirm: 'IMPORT_PAYMENT_METHODS',
    scopeLabel: 'encrypted payment methods',
    dependsOn: 'Users',
    notes: 'Imports user-level CloudPayments tokens only; raw tokens never appear in reports.',
    acceptsPagination: true,
  },
  {
    key: 'operations',
    label: 'Operation Action Log',
    sourceTypes: ['Operation'],
    endpoint: '/api/v1/admin/bubble-migration/operations/import',
    confirm: 'IMPORT_OPERATIONS',
    scopeLabel: 'user action log',
    dependsOn: 'Option Sets, Users, Services, Global References',
    notes: 'Stores operations as action history; does not create payment rows by default.',
    acceptsPagination: true,
  },
  {
    key: 'payments',
    label: 'Payment Facts',
    sourceTypes: ['Payment'],
    endpoint: '/api/v1/admin/bubble-migration/payments/import',
    confirm: 'IMPORT_PAYMENT_FACTS',
    scopeLabel: 'payment source facts',
    dependsOn: 'Users, Operations',
    notes:
      'Preserves Bubble payment ids and provider evidence; payment-level tokens are not saved.',
    acceptsPagination: true,
  },
  {
    key: 'subscriptionHistory',
    label: 'Subscription History Projection',
    sourceTypes: ['Operation', 'Payment'],
    endpoint: '/api/v1/admin/bubble-migration/subscription-history/import',
    confirm: 'IMPORT_SUBSCRIPTION_HISTORY',
    scopeLabel: 'payments read model',
    dependsOn: 'Operations, Payment Facts, Global References',
    notes:
      'Builds current subscription-page history from imported operations/payment facts; no-card and Restore price stay provider-less.',
    acceptsPagination: true,
  },
  {
    key: 'supportHistory',
    label: 'Support / Referral / Points History',
    sourceTypes: ['Promocode Used', 'Referral Link', 'Points History', 'Log', 'Email log'],
    endpoint: '/api/v1/admin/bubble-migration/support-history/import',
    confirm: 'IMPORT_SUPPORT_HISTORY',
    scopeLabel: 'support history',
    dependsOn: 'Users, Global References',
    notes:
      'Stores sanitized support, referral, points, promocode usage, and email-click history source rows.',
    acceptsPagination: true,
  },
]

const runEntityImport = ({ action, mode, pageLimit, maxPagesPerSource }: EntityImportInput) => {
  const body: Record<string, unknown> = { mode, runInBackground: true }

  if (action.acceptsPagination) {
    if (action.key === 'subscriptionHistory') {
      body.limit = pageLimit * (maxPagesPerSource ?? 1)
    } else {
      body.pageLimit = pageLimit
      if (maxPagesPerSource) {
        body.maxPagesPerSource = maxPagesPerSource
      }
    }
  }

  if (action.key === 'references') {
    body.sourceTypes = action.sourceTypes
  }

  if (action.key === 'supportHistory') {
    body.sourceTypes = action.sourceTypes
  }

  if (mode === 'live_run') {
    body.confirm = action.confirm
  }

  return api.post<DryRunResponse>(action.endpoint, body)
}

export function SuperAdminBubbleMigrationPage() {
  const { logout } = useAuth()
  const [activeConsoleTab, setActiveConsoleTab] = useState<MigrationConsoleTab>('overview')
  const [selectedSourceTypes, setSelectedSourceTypes] = useState<string[] | null>(null)
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  const [selectedArtifactId, setSelectedArtifactId] = useState<string | null>(null)
  const [liveConfirm, setLiveConfirm] = useState('')
  const [dryRunForm, setDryRunForm] = useState<DryRunFormState>({
    pageLimit: '100',
    maxPagesPerSource: '',
    scanDepth: 'first_page',
  })

  const overviewQuery = useQuery({
    queryKey: ['super-admin-bubble-migration-overview'],
    queryFn: loadOverview,
    refetchInterval: (query) => {
      const activeJob = query.state.data?.data.activeJob
      return activeJob?.status === 'queued' ||
        activeJob?.status === 'running' ||
        activeJob?.status === 'cancel_requested'
        ? 5000
        : false
    },
  })

  const overview = overviewQuery.data?.data
  const dataApiSourceCatalog = useMemo(
    () => overview?.sourceCatalog.filter((source) => source.sourceType !== 'OptionSet') ?? [],
    [overview?.sourceCatalog]
  )
  const allSourceTypes = dataApiSourceCatalog.map((source) => source.sourceType)
  const effectiveSelectedSourceTypes = selectedSourceTypes ?? allSourceTypes

  const selectedRunDetailQuery = useQuery({
    queryKey: ['super-admin-bubble-migration-run-detail', selectedRunId],
    queryFn: () => loadRunDetail(selectedRunId as string),
    enabled: Boolean(selectedRunId),
  })

  const reportDetailQuery = useQuery({
    queryKey: ['super-admin-bubble-migration-report-detail', selectedArtifactId],
    queryFn: () => loadReportDetail(selectedArtifactId as string),
    enabled: Boolean(selectedArtifactId),
  })

  const dryRunMutation = useMutation({
    mutationFn: runDryRun,
    onSuccess: async (response) => {
      if (response.data.runId) {
        setSelectedRunId(response.data.runId)
      }
      await queryClient.invalidateQueries({ queryKey: ['super-admin-bubble-migration-overview'] })
      if (response.data.runId) {
        await queryClient.invalidateQueries({
          queryKey: ['super-admin-bubble-migration-run-detail', response.data.runId],
        })
      }
    },
  })

  const entityImportMutation = useMutation({
    mutationFn: runEntityImport,
    onSuccess: async (response) => {
      if (response.data.runId) {
        setSelectedRunId(response.data.runId)
      }
      await queryClient.invalidateQueries({ queryKey: ['super-admin-bubble-migration-overview'] })
      if (response.data.runId) {
        await queryClient.invalidateQueries({
          queryKey: ['super-admin-bubble-migration-run-detail', response.data.runId],
        })
      }
    },
  })

  const cancelJobMutation = useMutation({
    mutationFn: cancelMigrationJob,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['super-admin-bubble-migration-overview'] })
    },
  })

  const groupedSources = useMemo(() => {
    const groups = new Map<MigrationSource['category'], MigrationSource[]>()
    for (const source of dataApiSourceCatalog) {
      groups.set(source.category, [...(groups.get(source.category) ?? []), source])
    }
    return Array.from(groups.entries())
  }, [dataApiSourceCatalog])

  const latestDryRunId = overview?.latestDryRun?.id
  const shownRun = selectedRunDetailQuery.data?.data.run ?? overview?.latestDryRun ?? null
  const sourceCountTotal = overview?.sourceCatalog.reduce(
    (total, source) => total + (source.sourceCount ?? 0),
    0
  )
  const localImportedTotal = overview?.sourceCatalog.reduce(
    (total, source) => total + source.localImportedCount,
    0
  )

  const toggleSource = (sourceType: string, checked: boolean) => {
    setSelectedSourceTypes((prev) => {
      const current = prev ?? allSourceTypes
      return checked
        ? Array.from(new Set([...current, sourceType]))
        : current.filter((currentSource) => currentSource !== sourceType)
    })
  }

  const selectAllSources = () => {
    setSelectedSourceTypes(allSourceTypes)
  }

  const clearAllSources = () => {
    setSelectedSourceTypes([])
  }

  const resetSourceSelection = () => {
    setSelectedSourceTypes(null)
  }

  const handleRunDryRun = () => {
    const pageLimit = Number(dryRunForm.pageLimit)
    const maxPages =
      dryRunForm.scanDepth === 'first_page'
        ? 1
        : dryRunForm.maxPagesPerSource.trim()
          ? Number(dryRunForm.maxPagesPerSource)
          : undefined

    dryRunMutation.mutate({
      sourceTypes: effectiveSelectedSourceTypes,
      pageLimit: Number.isFinite(pageLimit) ? pageLimit : 100,
      maxPagesPerSource: maxPages && Number.isFinite(maxPages) ? maxPages : undefined,
      runInBackground: true,
    })
  }

  const renderError = () => {
    const error =
      overviewQuery.error ??
      selectedRunDetailQuery.error ??
      reportDetailQuery.error ??
      dryRunMutation.error ??
      entityImportMutation.error ??
      null
    if (!error) {
      return null
    }

    const message =
      error instanceof ApiError
        ? `${error.status} ${error.statusText}`
        : 'Unable to load Bubble migration data'

    return (
      <div
        style={{
          borderRadius: designTokens.borders.radius.sm,
          backgroundColor: '#fee2e2',
          color: '#7f1d1d',
          padding: designTokens.spacing.md,
          fontFamily: designTokens.typography.fontFamilies.primary,
          ...typographyStyle(designTokens.typography.body.base),
        }}
      >
        {message}
      </div>
    )
  }

  const importPageLimit = Number(dryRunForm.pageLimit)
  const effectiveImportPageLimit = Number.isFinite(importPageLimit) ? importPageLimit : 100
  const importMaxPages = dryRunForm.maxPagesPerSource.trim()
    ? Number(dryRunForm.maxPagesPerSource)
    : undefined
  const effectiveImportMaxPages =
    importMaxPages && Number.isFinite(importMaxPages) ? importMaxPages : undefined

  const selectedImportActions = entityImportActions.map((action) => ({
    ...action,
    selected: true,
  }))
  const activeJob = overview?.activeJob ?? null
  const activeJobBlocking =
    activeJob?.status === 'queued' ||
    activeJob?.status === 'running' ||
    activeJob?.status === 'cancel_requested' ||
    activeJob?.status === 'stale'
  const activeJobCancellable = activeJob?.status === 'queued' || activeJob?.status === 'running'
  const activeJobProgress = activeJob?.progress ?? {}
  const progressText = (key: string, fallback = '—') => {
    const value = activeJobProgress[key]
    if (value === null || value === undefined || value === '') {
      return fallback
    }
    return String(value)
  }
  const activeJobSourceLabel =
    progressText('currentSource', '') || progressText('lastCompletedSource', '—')

  const latestRunCards: Array<[string, string]> = [
    ['Latest run', shownRun ? `${shownRun.mode} · ${shownRun.status}` : 'No runs'],
    ['Bubble rows in latest run', formatNumber(sourceCountTotal)],
    ['Latest dry-run', overview?.latestDryRun ? overview.latestDryRun.status : 'No dry-run'],
    ['Relations pending', formatNumber(overview?.relationSummary.pending ?? 0)],
  ]

  const localStateCards: Array<[string, string]> = [
    ['Users with Bubble id', formatNumber(overview?.localTotals.usersWithBubbleId ?? 0)],
    ['Services with Bubble id', formatNumber(overview?.localTotals.servicesWithBubbleId ?? 0)],
    ['Prices with Bubble id', formatNumber(overview?.localTotals.pricesWithBubbleId ?? 0)],
    ['Operation facts', formatNumber(overview?.localTotals.operationEvents ?? 0)],
    ['Payment facts', formatNumber(overview?.localTotals.paymentFacts ?? 0)],
    ['Payment projections', formatNumber(overview?.localTotals.paymentProjections ?? 0)],
    ['Local source/reference rows', formatNumber(localImportedTotal)],
    ['Selected sources', formatNumber(effectiveSelectedSourceTypes.length)],
  ]

  const activeJobCards: Array<[string, string]> = activeJob
    ? [
        ['Job', `${activeJob.operation} · ${activeJob.status}`],
        ['Sources', formatNumber(activeJob.sourceTypes.length)],
        ['Page limit', formatNumber(activeJob.pageLimit)],
        ['Run id', activeJob.runId ?? 'Pending'],
        ['Heartbeat', activeJob.heartbeatAt ? formatDate(activeJob.heartbeatAt) : 'Pending'],
        [
          'Current source',
          `${activeJobSourceLabel} (${progressText('currentSourceIndex', '0')}/${progressText('sourcesTotal', '0')})`,
        ],
        ['Current page', progressText('currentPage', '0')],
        ['Records seen', progressText('recordsSeen', '0')],
        ['Source expected rows', progressText('currentSourceExpectedRecords')],
        ['Source remaining rows', progressText('currentSourceRemainingRecords')],
      ]
    : []

  const relationEntries = Object.entries(overview?.relationSummary ?? {}).sort(([left], [right]) =>
    left.localeCompare(right)
  )
  const latestRunResults = selectedRunDetailQuery.data?.data.entityResults ?? []
  const conflictResults = latestRunResults.filter(
    (item) => item.status === 'conflict' || item.action === 'conflict' || item.conflictCode
  )
  const failedResults = latestRunResults.filter(
    (item) => item.status === 'failed' || item.action === 'failed'
  )
  const visibleAuditEvents =
    selectedRunDetailQuery.data?.data.auditEvents ?? overview?.auditEvents ?? []
  const visibleReportArtifacts =
    selectedRunDetailQuery.data?.data.reportArtifacts ?? overview?.reportArtifacts ?? []

  const renderMetricCards = (cards: Array<[string, string]>) => (
    <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      {cards.map(([label, value]) => (
        <div
          key={label}
          className="rounded-3xl border p-4"
          style={{
            borderColor: designTokens.colors.border.adminPattern,
            backgroundColor: designTokens.colors.background.surface,
          }}
        >
          <div style={mutedTextStyle}>{label}</div>
          <div
            style={{
              fontFamily: designTokens.typography.fontFamilies.primary,
              color: designTokens.colors.text.strong,
              ...typographyStyle(designTokens.typography.heading.h2),
            }}
          >
            {value}
          </div>
        </div>
      ))}
    </section>
  )

  return (
    <section
      className="mx-auto flex w-full max-w-[1360px] flex-col px-4 py-6 lg:px-8 lg:py-8"
      style={{ gap: designTokens.spacing.lg }}
    >
      <header className="flex w-full flex-col" style={{ gap: designTokens.spacing.sm }}>
        <div
          className="flex flex-wrap items-center justify-between"
          style={{ gap: designTokens.spacing.sm }}
        >
          <Link to={routes.dashboardLk}>
            <Button type="button" variant="secondaryAssist">
              Back To Dashboard
            </Button>
          </Link>
          <Button type="button" variant="secondaryAssist" onClick={logout}>
            Logout
          </Button>
        </div>
        <SuperAdminTabNav activeTab="migration" />
        <h1 style={titleStyle}>Bubble Migration</h1>
        <p style={subtitleStyle}>
          Source manifests, local counts, dry-run reports, conflicts, and audit trail for the Bubble
          data import.
        </p>
      </header>

      {renderError()}

      <nav className="flex flex-wrap gap-2">
        {migrationConsoleTabs.map((tab) => {
          const active = activeConsoleTab === tab.key
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveConsoleTab(tab.key)}
              className="rounded-2xl border px-4 py-2"
              style={{
                borderColor: active
                  ? designTokens.colors.brand.primary
                  : designTokens.colors.border.adminPattern,
                backgroundColor: active
                  ? designTokens.colors.brand.primary
                  : designTokens.colors.background.surface,
                color: active ? designTokens.colors.text.inverse : designTokens.colors.text.primary,
                fontFamily: designTokens.typography.fontFamilies.primary,
                ...typographyStyle(designTokens.typography.body.sm),
              }}
            >
              {tab.label}
            </button>
          )
        })}
      </nav>

      {activeConsoleTab === 'overview' ? (
        <div className="flex flex-col gap-4">
          {activeJob ? (
            <section className="flex flex-col gap-3">
              <div>
                <h2 style={sectionTitleStyle}>Backend Job</h2>
                <p style={mutedTextStyle}>
                  Full scans run on the backend. You can close or refresh this page; conflicting
                  migration controls stay locked while the job is queued or running.
                </p>
              </div>
              {renderMetricCards(activeJobCards)}
              {activeJobProgress.lastMessage ? (
                <div
                  className="rounded-2xl border p-3"
                  style={{
                    borderColor: designTokens.colors.border.adminPattern,
                    backgroundColor: designTokens.colors.background.surface,
                  }}
                >
                  <span style={mutedTextStyle}>{String(activeJobProgress.lastMessage)}</span>
                </div>
              ) : null}
              {activeJobCancellable ? (
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    type="button"
                    variant="secondaryAssist"
                    disabled={cancelJobMutation.isPending}
                    onClick={() => cancelJobMutation.mutate(activeJob.jobId)}
                  >
                    {cancelJobMutation.isPending ? 'Requesting…' : 'Request Stop'}
                  </Button>
                  <span style={mutedTextStyle}>
                    The backend records a cancellation request. Long importers stop only after the
                    current backend step supports cooperative cancellation.
                  </span>
                </div>
              ) : null}
              {activeJob.errorMessage ? (
                <div
                  className="rounded-2xl border p-3"
                  style={{
                    borderColor: '#fecaca',
                    backgroundColor: '#fee2e2',
                    color: '#7f1d1d',
                    fontFamily: designTokens.typography.fontFamilies.primary,
                    ...typographyStyle(designTokens.typography.body.sm),
                  }}
                >
                  {activeJob.errorMessage}
                </div>
              ) : null}
            </section>
          ) : null}
          <section className="flex flex-col gap-3">
            <div>
              <h2 style={sectionTitleStyle}>Latest Run</h2>
            </div>
            {renderMetricCards(latestRunCards)}
          </section>
          <section className="flex flex-col gap-3">
            <div>
              <h2 style={sectionTitleStyle}>Current Local Database</h2>
            </div>
            {renderMetricCards(localStateCards)}
          </section>
        </div>
      ) : null}

      {activeConsoleTab === 'sources' ? (
        <section
          className="rounded-3xl border p-4"
          style={{
            borderColor: designTokens.colors.border.adminPattern,
            backgroundColor: designTokens.colors.background.surface,
          }}
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex flex-col gap-1">
              <h2 style={sectionTitleStyle}>Source Scan Dry-Run</h2>
              <span style={mutedTextStyle}>
                Data API sources selected: {formatNumber(effectiveSelectedSourceTypes.length)}
              </span>
              <span style={mutedTextStyle}>
                Option Sets are imported from local sanitized artifacts in Import Steps, not scanned
                through Bubble Data API.
              </span>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex min-w-[140px] flex-col gap-2">
                <span style={tableHeadStyle}>Page limit</span>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={dryRunForm.pageLimit}
                  onChange={(event) =>
                    setDryRunForm((prev) => ({ ...prev, pageLimit: event.target.value }))
                  }
                  style={inputStyle}
                />
              </label>
              <label className="flex min-w-[190px] flex-col gap-2">
                <span style={tableHeadStyle}>Scan depth</span>
                <select
                  value={dryRunForm.scanDepth}
                  onChange={(event) =>
                    setDryRunForm((prev) => ({
                      ...prev,
                      scanDepth: event.target.value as DryRunFormState['scanDepth'],
                    }))
                  }
                  style={inputStyle}
                >
                  <option value="all_pages">Auto until empty</option>
                  <option value="first_page">First page only</option>
                </select>
              </label>
              <Button
                type="button"
                disabled={
                  dryRunMutation.isPending ||
                  activeJobBlocking ||
                  !overview?.actions.dryRunEnabled ||
                  effectiveSelectedSourceTypes.length === 0
                }
                onClick={handleRunDryRun}
              >
                {dryRunMutation.isPending || activeJobBlocking ? 'Running…' : 'Run Source Scan'}
              </Button>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <Button type="button" variant="secondaryAssist" onClick={selectAllSources}>
              Select All
            </Button>
            <Button type="button" variant="secondaryAssist" onClick={clearAllSources}>
              Clear All
            </Button>
            <Button type="button" variant="secondaryAssist" onClick={resetSourceSelection}>
              Reset Selection
            </Button>
            {latestDryRunId ? (
              <Button
                type="button"
                variant="secondaryAssist"
                onClick={() => setSelectedRunId(latestDryRunId)}
              >
                Latest Dry-Run
              </Button>
            ) : null}
            <Button
              type="button"
              variant="secondaryAssist"
              onClick={() => void overviewQuery.refetch()}
            >
              Refresh Overview Counts
            </Button>
            <span className="self-center" style={mutedTextStyle}>
              Refresh only reloads stored backend counters; it does not call Bubble.
            </span>
          </div>
          <div
            className="mt-4 rounded-2xl border p-3"
            style={{
              borderColor: designTokens.colors.border.adminPattern,
              backgroundColor: '#fef9c3',
              color: '#713f12',
              fontFamily: designTokens.typography.fontFamilies.primary,
              ...typographyStyle(designTokens.typography.body.sm),
            }}
          >
            Source Scan does not resolve option-set mappings. Run Import Steps -&gt; Option Sets
            before live imports that depend on statuses, operation types, tariffs, service types, or
            Bubble option values.
          </div>
        </section>
      ) : null}

      {activeConsoleTab === 'importSteps' ? (
        <section
          className="overflow-hidden rounded-3xl border"
          style={{
            borderColor: designTokens.colors.border.adminPattern,
            backgroundColor: designTokens.colors.background.surface,
          }}
        >
          <div className="flex flex-col gap-3 px-4 py-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex flex-col gap-1">
              <h2 style={sectionTitleStyle}>Ordered Import Steps</h2>
              <span style={mutedTextStyle}>
                Run these from top to bottom. For each step, run Dry-Run first, review reports and
                conflicts, then run Live Run when backend live mode is enabled.
              </span>
            </div>
            <label className="flex min-w-[240px] flex-col gap-2">
              <span style={tableHeadStyle}>Live confirmation</span>
              <input
                type="text"
                value={liveConfirm}
                onChange={(event) => setLiveConfirm(event.target.value)}
                placeholder="Type importer confirmation"
                style={inputStyle}
              />
            </label>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
              <thead>
                <tr>
                  <th align="left" style={cellStyle}>
                    <span style={tableHeadStyle}>Step</span>
                  </th>
                  <th align="left" style={cellStyle}>
                    <span style={tableHeadStyle}>Importer</span>
                  </th>
                  <th align="left" style={cellStyle}>
                    <span style={tableHeadStyle}>Scope</span>
                  </th>
                  <th align="left" style={cellStyle}>
                    <span style={tableHeadStyle}>Depends On</span>
                  </th>
                  <th align="left" style={cellStyle}>
                    <span style={tableHeadStyle}>Source Selection</span>
                  </th>
                  <th align="left" style={cellStyle}>
                    <span style={tableHeadStyle}>Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {selectedImportActions.map((action, index) => {
                  const dryRunDisabled =
                    entityImportMutation.isPending ||
                    activeJobBlocking ||
                    !overview?.actions.dryRunEnabled ||
                    !action.selected
                  const liveRunDisabled =
                    entityImportMutation.isPending ||
                    activeJobBlocking ||
                    !overview?.actions.liveRunEnabled ||
                    !action.selected ||
                    liveConfirm !== action.confirm

                  return (
                    <tr key={action.key}>
                      <td style={cellStyle}>
                        <strong>{index + 1}</strong>
                      </td>
                      <td style={cellStyle}>
                        <div className="flex min-w-[220px] flex-col gap-1">
                          <strong>{action.label}</strong>
                          <span style={mutedTextStyle}>{action.endpoint}</span>
                        </div>
                      </td>
                      <td style={cellStyle}>
                        <div className="flex min-w-[220px] flex-col gap-1">
                          <span>{action.scopeLabel}</span>
                          <span style={mutedTextStyle}>{action.notes}</span>
                        </div>
                      </td>
                      <td style={cellStyle}>
                        <div className="flex min-w-[220px] flex-col gap-1">
                          <span>{action.dependsOn}</span>
                        </div>
                      </td>
                      <td style={cellStyle}>
                        <div className="flex min-w-[180px] flex-col gap-1">
                          <span>Required for this step</span>
                          <span style={mutedTextStyle}>
                            {action.sourceTypes.length > 0 ? action.sourceTypes.join(', ') : '—'}
                          </span>
                        </div>
                      </td>
                      <td style={cellStyle}>
                        <div className="flex min-w-[280px] flex-wrap gap-2">
                          <Button
                            type="button"
                            variant="secondaryAssist"
                            disabled={dryRunDisabled}
                            onClick={() =>
                              entityImportMutation.mutate({
                                action,
                                mode: 'dry_run',
                                pageLimit: effectiveImportPageLimit,
                                maxPagesPerSource: effectiveImportMaxPages,
                              })
                            }
                          >
                            {entityImportMutation.isPending ? 'Running…' : 'Dry-Run'}
                          </Button>
                          <Button
                            type="button"
                            variant="secondaryAssist"
                            disabled={liveRunDisabled}
                            onClick={() =>
                              entityImportMutation.mutate({
                                action,
                                mode: 'live_run',
                                pageLimit: effectiveImportPageLimit,
                                maxPagesPerSource: effectiveImportMaxPages,
                              })
                            }
                          >
                            Live Run
                          </Button>
                          <span className="self-center" style={mutedTextStyle}>
                            Confirm: {action.confirm}
                          </span>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {activeConsoleTab === 'deltaSync' ? (
        <section
          className="rounded-3xl border p-4"
          style={{
            borderColor: designTokens.colors.border.adminPattern,
            backgroundColor: designTokens.colors.background.surface,
          }}
        >
          <div className="flex flex-col gap-3">
            <h2 style={sectionTitleStyle}>Delta Sync</h2>
            <p style={mutedTextStyle}>
              Post-import change checks will live here. The backend should store a high-water mark
              per Bubble source type and check Bubble Data API with Modified Date constraints so
              large sources such as Operation are not fully rescanned after every import.
            </p>
            <div
              className="rounded-2xl border p-3"
              style={{
                borderColor: designTokens.colors.border.adminPattern,
                backgroundColor: '#f8fafc',
              }}
            >
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                {[
                  ['Check All Changes', 'planned'],
                  ['Delta Dry-Run For Changed', 'planned'],
                  ['Delta Live-Run For Reviewed', 'planned'],
                  ['Check Single Record', 'planned'],
                ].map(([label, status]) => (
                  <div key={label} className="flex flex-col gap-1 border-b py-2">
                    <strong>{label}</strong>
                    <span style={mutedTextStyle}>{status}</span>
                  </div>
                ))}
              </div>
            </div>
            <span style={mutedTextStyle}>
              Planned row fields: source type, last successful import, saved max Modified Date,
              changed Bubble rows, latest source Modified Date, status, and per-source actions.
            </span>
          </div>
        </section>
      ) : null}

      {activeConsoleTab === 'sources' ? (
        <section
          className="overflow-hidden rounded-3xl border"
          style={{
            borderColor: designTokens.colors.border.adminPattern,
            backgroundColor: designTokens.colors.background.surface,
          }}
        >
          <div className="flex flex-wrap items-center justify-between px-4 py-3">
            <h2 style={sectionTitleStyle}>Entities</h2>
            <span style={mutedTextStyle}>
              Relations pending: {formatNumber(overview?.relationSummary.pending ?? 0)}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
              <thead>
                <tr>
                  <th align="left" style={cellStyle}>
                    <span style={tableHeadStyle}>Source</span>
                  </th>
                  <th align="left" style={cellStyle}>
                    <span style={tableHeadStyle}>Counts</span>
                  </th>
                  <th align="left" style={cellStyle}>
                    <span style={tableHeadStyle}>Latest Source Date</span>
                  </th>
                  <th align="left" style={cellStyle}>
                    <span style={tableHeadStyle}>Latest Run Actions</span>
                  </th>
                  <th align="left" style={cellStyle}>
                    <span style={tableHeadStyle}>Scope</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {groupedSources.flatMap(([, sources]) =>
                  sources.map((source) => {
                    const checked = effectiveSelectedSourceTypes.includes(source.sourceType)
                    return (
                      <tr key={source.sourceType}>
                        <td style={cellStyle}>
                          <div className="flex min-w-[220px] flex-col gap-1">
                            <strong>{source.label}</strong>
                            <span style={mutedTextStyle}>{source.sourceType}</span>
                            <span
                              className="inline-flex w-fit rounded-full px-2 py-1"
                              style={statusBadgeStyle(source.category)}
                            >
                              {source.category}
                            </span>
                          </div>
                        </td>
                        <td style={cellStyle}>
                          <div className="flex min-w-[170px] flex-col gap-1">
                            <span>Bubble: {formatNumber(source.sourceCount)}</span>
                            <span>Local: {formatNumber(source.localImportedCount)}</span>
                            <span style={mutedTextStyle}>
                              Identity map: {formatNumber(source.identityMapCount)}
                            </span>
                          </div>
                        </td>
                        <td style={cellStyle}>
                          <div className="flex min-w-[190px] flex-col gap-1">
                            <span>Created: {formatDate(source.latestSourceCreatedAt)}</span>
                            <span style={mutedTextStyle}>
                              Modified: {formatDate(source.latestSourceModifiedAt)}
                            </span>
                          </div>
                        </td>
                        <td style={cellStyle}>
                          <span>{actionSummary(source.latestRunSummary)}</span>
                        </td>
                        <td style={cellStyle}>
                          <div className="flex min-w-[260px] flex-col gap-2">
                            <Checkbox
                              checked={checked}
                              onChange={(event) =>
                                toggleSource(source.sourceType, event.target.checked)
                              }
                              label={
                                checked
                                  ? 'Selected for source scan'
                                  : 'Not selected for source scan'
                              }
                            />
                            <span style={mutedTextStyle}>{source.localStorage}</span>
                            <span style={mutedTextStyle}>{source.notes}</span>
                          </div>
                        </td>
                      </tr>
                    )
                  })
                )}
                {!dataApiSourceCatalog.length && !overviewQuery.isLoading ? (
                  <tr>
                    <td colSpan={5} style={cellStyle}>
                      No Bubble Data API sources configured.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {activeConsoleTab === 'runsReports' ? (
        <section className="grid gap-4 xl:grid-cols-[1fr_0.8fr]">
          <div
            className="overflow-hidden rounded-3xl border"
            style={{
              borderColor: designTokens.colors.border.adminPattern,
              backgroundColor: designTokens.colors.background.surface,
            }}
          >
            <div className="flex flex-wrap items-center justify-between px-4 py-3">
              <h2 style={sectionTitleStyle}>Run Review</h2>
              {shownRun ? (
                <span className="rounded-full px-2 py-1" style={statusBadgeStyle(shownRun.status)}>
                  {shownRun.status}
                </span>
              ) : null}
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse">
                <tbody>
                  {shownRun ? (
                    [
                      ['Run ID', shownRun.id],
                      ['Mode', shownRun.mode],
                      ['Started', formatDate(shownRun.startedAt)],
                      ['Finished', formatDate(shownRun.finishedAt)],
                      ['Totals', JSON.stringify(shownRun.totals)],
                    ].map(([label, value]) => (
                      <tr key={label}>
                        <td style={cellStyle}>
                          <span style={tableHeadStyle}>{label}</span>
                        </td>
                        <td style={cellStyle}>{value}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td style={cellStyle}>No dry-run selected.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="max-h-[360px] overflow-auto border-t px-4 py-3">
              <h3 style={tableHeadStyle}>Entity Results</h3>
              <div className="mt-3 flex flex-col gap-2">
                {latestRunResults.slice(0, 80).map((item) => (
                  <div
                    key={item.id}
                    className="grid gap-2 rounded-2xl border p-3 md:grid-cols-[180px_140px_1fr]"
                    style={{ borderColor: designTokens.colors.border.adminPattern }}
                  >
                    <div>
                      <strong>{item.sourceType}</strong>
                      <div style={mutedTextStyle}>{item.sourceId ?? 'no source id'}</div>
                    </div>
                    <div>
                      <span
                        className="rounded-full px-2 py-1"
                        style={statusBadgeStyle(item.action)}
                      >
                        {item.action}
                      </span>
                    </div>
                    <div style={mutedTextStyle}>
                      {item.localTable ?? 'no local table'} {item.localId ?? ''}
                      {item.conflictCode ? ` · ${item.conflictCode}` : ''}
                    </div>
                  </div>
                ))}
                {selectedRunDetailQuery.isLoading ? (
                  <span style={mutedTextStyle}>Loading…</span>
                ) : null}
              </div>
            </div>
          </div>

          <div
            className="rounded-3xl border p-4"
            style={{
              borderColor: designTokens.colors.border.adminPattern,
              backgroundColor: designTokens.colors.background.surface,
            }}
          >
            <h2 style={sectionTitleStyle}>Reports</h2>
            <div className="mt-4 flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                {visibleReportArtifacts.map((report) => (
                  <div key={report.id} className="flex flex-col gap-1 border-b py-2">
                    <strong>{report.artifactType}</strong>
                    <span style={mutedTextStyle}>
                      {report.format} · {report.storageKind} · {formatDate(report.generatedAt)}
                    </span>
                    <span style={mutedTextStyle}>{report.contentHash ?? 'no hash'}</span>
                    <Button
                      type="button"
                      variant="secondaryAssist"
                      onClick={() => setSelectedArtifactId(report.id)}
                    >
                      Open Report
                    </Button>
                  </div>
                ))}
                {!visibleReportArtifacts.length ? (
                  <span style={mutedTextStyle}>No reports yet.</span>
                ) : null}
              </div>
              {selectedArtifactId ? (
                <div>
                  <h3 style={tableHeadStyle}>Report Detail</h3>
                  <pre
                    className="mt-2 max-h-[320px] overflow-auto rounded-2xl border p-3"
                    style={{
                      borderColor: designTokens.colors.border.adminPattern,
                      color: designTokens.colors.text.primary,
                      ...typographyStyle(designTokens.typography.body.xsNormal),
                    }}
                  >
                    {reportDetailQuery.isLoading
                      ? 'Loading...'
                      : JSON.stringify(reportDetailQuery.data?.data.contentJson ?? {}, null, 2)}
                  </pre>
                </div>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      {activeConsoleTab === 'conflicts' ? (
        <section className="grid gap-4 xl:grid-cols-[0.8fr_1.2fr]">
          <div
            className="rounded-3xl border p-4"
            style={{
              borderColor: designTokens.colors.border.adminPattern,
              backgroundColor: designTokens.colors.background.surface,
            }}
          >
            <h2 style={sectionTitleStyle}>Relation Queue</h2>
            <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-1">
              {relationEntries.map(([status, total]) => (
                <div key={status} className="flex items-center justify-between border-b py-2">
                  <span className="rounded-full px-2 py-1" style={statusBadgeStyle(status)}>
                    {status}
                  </span>
                  <strong>{formatNumber(total)}</strong>
                </div>
              ))}
              {!relationEntries.length ? (
                <span style={mutedTextStyle}>No relation queue rows.</span>
              ) : null}
            </div>
          </div>

          <div
            className="rounded-3xl border p-4"
            style={{
              borderColor: designTokens.colors.border.adminPattern,
              backgroundColor: designTokens.colors.background.surface,
            }}
          >
            <h2 style={sectionTitleStyle}>Latest Run Issues</h2>
            <div className="mt-4 flex flex-col gap-3">
              {[...conflictResults, ...failedResults].slice(0, 80).map((item) => (
                <div
                  key={item.id}
                  className="grid gap-2 rounded-2xl border p-3 md:grid-cols-[180px_160px_1fr]"
                  style={{ borderColor: designTokens.colors.border.adminPattern }}
                >
                  <div>
                    <strong>{item.sourceType}</strong>
                    <div style={mutedTextStyle}>{item.sourceId ?? 'no source id'}</div>
                  </div>
                  <span
                    className="h-fit rounded-full px-2 py-1"
                    style={statusBadgeStyle(item.status)}
                  >
                    {item.status}
                  </span>
                  <div style={mutedTextStyle}>
                    {item.conflictCode ?? item.action}
                    {item.conflictMessage ? ` · ${item.conflictMessage}` : ''}
                  </div>
                </div>
              ))}
              {!conflictResults.length && !failedResults.length ? (
                <span style={mutedTextStyle}>No conflicts or failed rows in the selected run.</span>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      {activeConsoleTab === 'audit' ? (
        <section
          className="rounded-3xl border p-4"
          style={{
            borderColor: designTokens.colors.border.adminPattern,
            backgroundColor: designTokens.colors.background.surface,
          }}
        >
          <h2 style={sectionTitleStyle}>Audit</h2>
          <div className="mt-4 flex max-h-[560px] flex-col gap-2 overflow-auto">
            {visibleAuditEvents.map((event) => (
              <div key={event.id} className="flex flex-col gap-1 border-b py-2">
                <strong>{event.eventType}</strong>
                <span>{event.message}</span>
                <span style={mutedTextStyle}>
                  {formatDate(event.createdAt)} · {event.actorUserId ?? 'system'}
                </span>
              </div>
            ))}
            {!visibleAuditEvents.length ? (
              <span style={mutedTextStyle}>No audit events.</span>
            ) : null}
          </div>
        </section>
      ) : null}

      {activeConsoleTab === 'diagnostics' ? (
        <section className="grid gap-4 xl:grid-cols-2">
          <div
            className="rounded-3xl border p-4"
            style={{
              borderColor: designTokens.colors.border.adminPattern,
              backgroundColor: designTokens.colors.background.surface,
            }}
          >
            <h2 style={sectionTitleStyle}>Payment Projection Diagnostics</h2>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {[
                [
                  'With operation id',
                  overview?.paymentLineageSummary.projectionsWithBubbleOperationId,
                ],
                [
                  'With Bubble payment id',
                  overview?.paymentLineageSummary.projectionsWithBubblePaymentId,
                ],
                [
                  'Needs lineage check',
                  overview?.paymentLineageSummary.projectionsMissingBubblePaymentId,
                ],
                ['Operation-derived', overview?.paymentLineageSummary.operationDerivedProjections],
              ].map(([label, value]) => (
                <div key={label} className="flex flex-col gap-1 border-b py-2">
                  <span style={mutedTextStyle}>{label}</span>
                  <strong>{formatNumber(typeof value === 'number' ? value : null)}</strong>
                </div>
              ))}
            </div>
          </div>

          <div
            className="rounded-3xl border p-4"
            style={{
              borderColor: designTokens.colors.border.adminPattern,
              backgroundColor: designTokens.colors.background.surface,
            }}
          >
            <h2 style={sectionTitleStyle}>Local Storage Counters</h2>
            <div className="mt-4 flex flex-col gap-2">
              {localStateCards.map(([label, value]) => (
                <div key={label} className="flex items-center justify-between border-b py-2">
                  <span style={mutedTextStyle}>{label}</span>
                  <strong>{value}</strong>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}
    </section>
  )
}

SuperAdminBubbleMigrationPage.displayName = 'SuperAdminBubbleMigrationPage'
